import React, { KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { services } from '../../data/services';
import { LinkButton } from '../LinkButton';
import { SplitText } from '../SplitText';
import { ServiceRing } from './ServiceRing';
export function Services() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);

  // Track the selected description's height (including on resize) so the block can ease to it.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const update = () => setPanelHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);
  const select = (index: number) => {
    setActive(index);
    tabRefs.current[index]?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      select((active + 1) % services.length);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      select((active - 1 + services.length) % services.length);
    }
  };
  return <section id="services" className="bg-white px-5 py-[8rem] md:px-10 md:py-[10rem] lg:py-[12rem]" data-id="element-153" data-mp="1axsj69h0uzoe">
      <div className="mx-auto flex max-w-[71.875rem] flex-col items-center" data-id="element-154" data-mp="23oasvtc93zas">
        <div className="mb-[3.125rem] grid w-full grid-cols-1 items-center sm:mb-[3.75rem] lg:grid-cols-2" data-id="element-155" data-mp="23m6g0lwdjpyd">
          <div className="max-w-[20rem] sm:max-w-[32.8125rem]" data-id="element-156" data-mp="1yr0w1smj8lcx">
            <SplitText as="h2" text="End-to-end solutions that produce 'go crazy' good impressions" className="text-[2rem] font-medium leading-[1.34] tracking-[0.47px] md:text-[3.063rem] md:tracking-[0.7px]" data-id="element-157" data-mp="1tpbio6bu7vlo" />
          </div>
          <div className="hidden items-center justify-center lg:flex" data-id="element-158" data-mp="jrvazshjw7w4">
            <ServiceRing rotation={services[active].degrees} data-id="element-159" data-mp="oqy4qgs2yros" />
          </div>
        </div>

        <div className="h-px w-full bg-line" data-id="element-160" data-mp="arfamrw3tgvh" />

        <div className="mb-[6.25rem] mt-[3.125rem] flex w-full flex-col sm:mb-[8rem] sm:mt-[3.75rem] sm:flex-row sm:justify-start lg:justify-end" data-id="element-161" data-mp="aqnbx877n30z">
          <div role="tablist" aria-orientation="vertical" aria-label="Services" onKeyDown={onKeyDown} className="mb-[3.2rem] flex flex-none flex-col items-start gap-[0.829rem] sm:mb-0 sm:mr-10 sm:gap-5 md:mr-16 lg:mr-[10rem]" data-id="element-162" data-mp="11lynps1uzqp">
            {services.map((service, i) => {
            const isActive = i === active;
            return <button key={service.title} ref={el => tabRefs.current[i] = el} type="button" role="tab" id={`service-tab-${i}`} aria-selected={isActive} aria-controls={`service-panel-${i}`} tabIndex={isActive ? 0 : -1} onClick={() => setActive(i)} className={`group flex items-center gap-[0.625rem] text-left transition-colors duration-[250ms] ease-[cubic-bezier(0.23,1,0.32,1)] md:gap-[0.9375rem] ${isActive ? 'text-brand' : 'text-black/40 hover:text-black'}`} data-id="element-163" data-mp="3e8fdneb5f1cd" data-mpi={i}>
                  {/* Hover: the badge softly deepens and the label glides a few pixels right — no sliding fill. */}
                  <span className={`relative flex h-5 w-5 flex-none items-center justify-center rounded-full transition-[background-color,color] duration-[250ms] ease-[cubic-bezier(0.23,1,0.32,1)] sm:h-6 sm:w-6 ${isActive ? 'bg-brand text-white' : 'bg-chip text-black/60 group-hover:bg-black/10 group-hover:text-black'}`} data-id="element-164" data-mp="3s92omo9s95ix">
                    <span className="relative text-[0.813rem] font-medium leading-none sm:text-[0.875rem]" data-id="element-165" data-mp="1ulialvaxb3yo">{i + 1}</span>
                  </span>
                  <span className={`whitespace-nowrap text-[1rem] font-medium leading-[1.25] tracking-[0.01em] transition-transform duration-[250ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none sm:text-[1.25rem] ${isActive ? '' : 'group-hover:translate-x-1'}`} data-id="element-166" data-mp="1zja60zas187m">
                    {service.title}
                  </span>
                </button>;
          })}
          </div>

          {/* Only the selected description is shown; the block's height eases to fit it,
              so short descriptions don't leave a large empty gap above "View Services". */}
          <motion.div animate={{
          height: panelHeight ?? 'auto'
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="relative w-full overflow-hidden sm:w-[20rem] md:w-[28rem] lg:w-[36rem] lg:flex-none" data-id="element-167" data-mp="2x0r0pw1xvu3l">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-168" data-mp="leq7enr5itpl">
              <motion.div key={active} ref={panelRef} id={`service-panel-${active}`} role="tabpanel" aria-labelledby={`service-tab-${active}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1,
              transition: {
                duration: 0.25,
                ease: 'easeOut'
              }
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.1,
                ease: 'easeOut'
              }
            }} data-id="element-169" data-mp="gmypt2pz3ybl">
                <p className="text-[1.25rem] font-normal leading-[1.4] tracking-[0.005em] text-black sm:text-[2rem]" data-id="element-170" data-mp="3tu0fqdlmwv5t">
                  {services[active].body}
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        <LinkButton label="View Services" href="#services" variant="muted" data-id="element-171" data-mp="1n2r9xy69nbyf" />
      </div>
    </section>;
}